/**
 * View models + data wiring for the Consolidated EOS Reporting view (End of Shift
 * Reporting). Data comes from compareFlowMonitoringData and is mapped into
 * EosShiftData here.
 */

import { compareFlowMonitoringData } from '../api';
import {
  CompareFlowMonitoringDataOutput,
  MonitoringEntityIdentityComparisonData,
  ShiftKpiComparison,
  ShiftTimeContext,
} from '../model';
import {
  MetricName,
  MonitoringEntityType,
  ProcessPathType,
  ShiftKpiType,
  VolumeTargetType,
} from '../constants';
import { EosStatus, comparisonStatusToEosStatus } from './eosReportingUtils';
import { getClockWindowNameBasedOnSelectedOption } from '../../../../services/shiftPlanService';

/** One process-path row. `vol`/`volTgt` are present for primary paths only. */
export interface EosPathPerf {
  name: string;
  vol?: number;
  volTgt?: number;
  rate: number;
  rateTgt: number;
  hc: number;
  hcTgt: number;
}

/** All data for a single shift (Day or Night). */
export interface EosShiftData {
  key: 'day' | 'night';
  label: string;
  timeRange: string;
  volume: number;
  volumeTarget: number;
  tph: number;
  tphOP: number;
  supportRate: number;
  supportTarget: number;
  hcStaffed: number;
  hcTarget: number;
  /** Server-computed card colors (from ShiftKpiComparison.comparisonStatus). Undefined → derive on the client. */
  volumeStatus?: EosStatus;
  tphStatus?: EosStatus;
  supportStatus?: EosStatus;
  hcStatus?: EosStatus;
  primaryPaths: EosPathPerf[];
  supportPaths: EosPathPerf[];
  wash: string;
  /** Shift window + server time for this shift; used to show the "In progress" pill. Undefined when the response omits it. */
  shiftTimeContext?: ShiftTimeContext;
}

/** Per-shift metadata; numeric fields and the wash text come from the compare response. */
type ShiftMeta = Pick<EosShiftData, 'key' | 'label' | 'timeRange'>;

/** Shown when the shift plan has no EOS wash report saved yet. */
const WASH_PLACEHOLDER = '—';

const DAY_META: ShiftMeta = {
  key: 'day',
  label: 'Day Shift',
  timeRange: '07:00 – 17:00',
};
const NIGHT_META: ShiftMeta = {
  key: 'night',
  label: 'Night Shift',
  timeRange: '19:00 – 05:00',
};

/** The two shift blocks for the EOS view. `null` = no data available yet. */
export interface EosReportingData {
  day: EosShiftData | null;
  night: EosShiftData | null;
  /**
   * Plan the backend compared against, echoed from the response metadata (day
   * preferred, night as fallback). Used to default the report's plan picker so it
   * shows the plan actually being reported on.
   */
  resolvedBaselinePlanId?: string;
}

const pathLabel = (e: MonitoringEntityIdentityComparisonData): string =>
  e.monitoringEntityIdentity.processPath?.displayName ||
  e.monitoringEntityIdentity.processPath?.functionName ||
  e.monitoringEntityIdentity.processPath?.processName ||
  'Unknown';

// Support/indirect classification. Prefer the authoritative pathType (INDIRECT) from the
// model; fall back to the process-path name containing "support" while the service rolls
// pathType out (mirrors the backend rule RnRRCCWarehouseConfigHelperImpl / G2S2Constants.SUPPORT).
const isSupportPath = (e: MonitoringEntityIdentityComparisonData): boolean => {
  const pathType = e.monitoringEntityIdentity.processPath?.pathType;
  if (pathType) return pathType === ProcessPathType.INDIRECT;
  return !!e.monitoringEntityIdentity.processPath?.processName?.toLowerCase().includes('support');
};

/** Finds a pre-computed KPI card in the response by type, if present. */
const kpiOf = (
  resp: CompareFlowMonitoringDataOutput,
  type: ShiftKpiType
): ShiftKpiComparison | undefined =>
  (resp.shiftKpiComparisons ?? []).find(k => k.kpiType === type);

/** actual/target for one metric on a comparison entity (0 if absent). */
const metricOf = (
  e: MonitoringEntityIdentityComparisonData,
  name: MetricName
): { actual: number; target: number } => {
  const m = e.metricsComparisonList.find(x => x.metricName === name);
  return { actual: m?.actualValue ?? 0, target: m?.expectedValue ?? 0 };
};

const toPathPerf = (
  e: MonitoringEntityIdentityComparisonData,
  includeVolume: boolean
): EosPathPerf => {
  const vol = metricOf(e, MetricName.VOLUME_PROCESSED);
  const rate = metricOf(e, MetricName.UPH);
  const hc = metricOf(e, MetricName.HEADCOUNT);
  return {
    name: pathLabel(e),
    ...(includeVolume ? { vol: vol.actual, volTgt: vol.target } : {}),
    rate: rate.actual,
    rateTgt: rate.target,
    hc: hc.actual,
    hcTgt: hc.target,
  };
};

/** Ratio rounded to 1 decimal; 0 when the denominator is 0. */
const ratio = (numerator: number, denominator: number): number =>
  denominator ? Math.round((numerator / denominator) * 10) / 10 : 0;

/**
 * Maps a shift's compareFlowMonitoringData response into an EosShiftData:
 * - KPI cards (Volume Completed / TPH / Support Rate / HC Staffed) + their colors from the
 *   server-computed `shiftKpiComparisons`; falls back to client derivation when absent.
 * - per-path Volume/Rate/HC from each entity; Primary/Support split from `pathType`.
 * - the EOS wash note from the response's `eosWashReport` (placeholder when none saved yet).
 * `meta` supplies key/label/timeRange.
 */
export function mapCompareToShift(
  resp: CompareFlowMonitoringDataOutput,
  meta: ShiftMeta
): EosShiftData {
  const entities = resp.shiftTotalsComparison ?? [];
  const primaryPaths: EosPathPerf[] = [];
  const supportPaths: EosPathPerf[] = [];
  let supVol = 0;
  let supVolTgt = 0;
  let supHc = 0;
  let supHcTgt = 0;
  entities.forEach(e => {
    if (isSupportPath(e)) {
      supportPaths.push(toPathPerf(e, false));
      supVol += metricOf(e, MetricName.VOLUME_PROCESSED).actual;
      supVolTgt += metricOf(e, MetricName.VOLUME_PROCESSED).target;
      supHc += metricOf(e, MetricName.HEADCOUNT).actual;
      supHcTgt += metricOf(e, MetricName.HEADCOUNT).target;
    } else {
      primaryPaths.push(toPathPerf(e, true));
    }
  });

  // Sequence each bucket by its metric total, highest first — matches the In-Shift
  // ProgressReportTable ordering. Primary is ranked by volume, support by headcount.
  /* istanbul ignore next -- vol is always set for primary paths (toPathPerf includeVolume); ?? 0 is defensive */
  const byVolumeDesc = (left: EosPathPerf, right: EosPathPerf) => (right.vol ?? 0) - (left.vol ?? 0);
  const byHeadcountDesc = (left: EosPathPerf, right: EosPathPerf) => right.hc - left.hc;
  primaryPaths.sort(byVolumeDesc);
  supportPaths.sort(byHeadcountDesc);

  const alps = (resp.volumeTargetComparisons ?? []).find(
    v => v.targetType === VolumeTargetType.ALPS_TARGET
  );
  const volume = alps?.actualValue ?? 0;
  const volumeTarget = alps?.targetValue ?? 0;
  const hcActual = entities.reduce((s, e) => s + metricOf(e, MetricName.HEADCOUNT).actual, 0);
  const hcTarget = entities.reduce((s, e) => s + metricOf(e, MetricName.HEADCOUNT).target, 0);

  // Prefer the server-computed KPI cards; fall back to the client derivation below while the
  // service rolls shiftKpiComparisons out (keeps older responses / envs rendering).
  const volumeKpi = kpiOf(resp, ShiftKpiType.VOLUME_COMPLETED);
  const tphKpi = kpiOf(resp, ShiftKpiType.TPH);
  const supportKpi = kpiOf(resp, ShiftKpiType.SUPPORT_RATE);
  const hcKpi = kpiOf(resp, ShiftKpiType.HC_STAFFED);

  return {
    ...meta,
    volume: volumeKpi?.actualValue ?? volume,
    volumeTarget: volumeKpi?.targetValue ?? volumeTarget,
    hcStaffed: hcKpi?.actualValue ?? hcActual,
    hcTarget: hcKpi?.targetValue ?? hcTarget,
    tph: tphKpi?.actualValue ?? ratio(volume, hcActual),
    tphOP: tphKpi?.targetValue ?? ratio(volumeTarget, hcTarget),
    supportRate: supportKpi?.actualValue ?? ratio(supVol, supHc),
    supportTarget: supportKpi?.targetValue ?? ratio(supVolTgt, supHcTgt),
    volumeStatus: comparisonStatusToEosStatus(volumeKpi?.comparisonStatus),
    tphStatus: comparisonStatusToEosStatus(tphKpi?.comparisonStatus),
    supportStatus: comparisonStatusToEosStatus(supportKpi?.comparisonStatus),
    hcStatus: comparisonStatusToEosStatus(hcKpi?.comparisonStatus),
    primaryPaths,
    supportPaths,
    // EOS wash note from the compare response's eosWashReport (server-fetched, keyed by the
    // shift plan). Placeholder when no report has been saved for this shift plan yet.
    wash: resp.eosWashReport?.content ?? WASH_PLACEHOLDER,
    shiftTimeContext: resp.shiftTimeContext,
  };
}

/**
 * Fetches EOS data by calling compareFlowMonitoringData for DAY and NIGHT.
 * Each call catches its own error and resolves to null, so one shift failing
 * still renders the other; both failing rejects.
 */
export async function fetchEosReportingData(
  warehouseId: string,
  ofdDate: string,
  /**
   * Plan chosen in the report's plan picker. Omitted until the operator picks one,
   * in which case the facade resolves the baseline itself as before.
   */
  baselineShiftPlanId?: string
): Promise<EosReportingData> {
  const fetchShift = (option: 'day' | 'night') =>
    compareFlowMonitoringData({
      warehouseId,
      ofdDate,
      shiftType: getClockWindowNameBasedOnSelectedOption(option),
      monitoringEntityType: MonitoringEntityType.PROCESS_PATH,
      baselineShiftPlanId,
    }).catch(() => null);

  const [dayResp, nightResp] = await Promise.all([fetchShift('day'), fetchShift('night')]);
  if (!dayResp && !nightResp) {
    throw new Error('Failed to fetch End of Shift data for both shifts');
  }
  // Empty payload (no paths) → null, so the view shows its empty state, not a zeroed block.
  return {
    day: dayResp?.shiftTotalsComparison?.length ? mapCompareToShift(dayResp, DAY_META) : null,
    night: nightResp?.shiftTotalsComparison?.length
      ? mapCompareToShift(nightResp, NIGHT_META)
      : null,
    resolvedBaselinePlanId:
      dayResp?.compareFlowMonitoringMetadata?.baselineShiftPlanId
      ?? nightResp?.compareFlowMonitoringMetadata?.baselineShiftPlanId,
  };
}
